import React, { useState, useEffect, useCallback } from "react";
import { CheckIcon, SpinnerIcon } from "@/components/icons";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

type PublicState = "private" | "copying" | "public";

const toState = (status: MediaItem["publicStatus"]): PublicState =>
  status === "public" ? "public" : status === "copying" ? "copying" : "private";

interface MakePublicActionProps {
  clientId: string;
  mediaItem: MediaItem;
}

export const MakePublicAction: React.FC<MakePublicActionProps> = ({
  clientId,
  mediaItem,
}) => {
  const [state, setState] = useState<PublicState>(toState(mediaItem.publicStatus));

  // Reset to the new item's status when navigating between items.
  useEffect(() => {
    setState(toState(mediaItem.publicStatus));
  }, [mediaItem.id, mediaItem.publicStatus]);

  const handleMakePublic = useCallback(async () => {
    setState("copying");
    try {
      const { imageActionsService } = await import(
        "@/core/MediaItem/ImageActionsService"
      );
      await imageActionsService.makePublic(clientId, mediaItem.id);
      setState("public");
    } catch (err) {
      console.error("Failed to make public:", err);
      setState("private");
    }
  }, [clientId, mediaItem.id]);

  const handleRemovePublic = useCallback(async () => {
    setState("copying");
    try {
      const { imageActionsService } = await import(
        "@/core/MediaItem/ImageActionsService"
      );
      await imageActionsService.removePublic(clientId, mediaItem.id);
      setState("private");
    } catch (err) {
      console.error("Failed to remove public link:", err);
      setState("public");
    }
  }, [clientId, mediaItem.id]);

  if (state === "copying") {
    return (
      <button
        disabled
        className="w-full flex items-center gap-2 px-3 py-2 rounded-sm bg-gray-800 text-gray-300 opacity-50 cursor-not-allowed"
      >
        <SpinnerIcon className="w-4 h-4" />
        Publishing...
      </button>
    );
  }

  if (state === "public") {
    return (
      <div className="flex items-center gap-2">
        <span className="flex-1 flex items-center gap-2 px-3 py-2 rounded-sm bg-cyan-900/50 text-cyan-300">
          <CheckIcon className="w-4 h-4" />
          Public
        </span>
        <button
          onClick={handleRemovePublic}
          className="px-3 py-2 rounded-sm bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm transition-colors"
        >
          Make Private
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleMakePublic}
      className="w-full flex items-center justify-start px-3 py-2 rounded-sm bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
    >
      Make Public
    </button>
  );
};
